import { useState, useEffect } from 'react'
import './ShareModal.css'
import { AccessListSection } from './AccessListSection'
import { UserAvatar } from './UserAvatar'
import Toast from './Toast'
import { useSyncStatus } from '../hooks/useSyncStatus'
import { httpClient } from '../../api/http'

interface ShareModalProps {
  scenarioId: string
  scenarioName: string
  canManageInvites: boolean
  onClose: () => void
}

interface User {
  userId: string
  email: string
  name?: string
  role: 'Owner' | 'Editor'
}

interface Invite {
  inviteId: string
  email: string
  createdAt: string
  invitedBy: string
}

export function ShareModal({ scenarioId, scenarioName, canManageInvites, onClose }: ShareModalProps) {
  const [email, setEmail] = useState('')
  const [isInviting, setIsInviting] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{
    message: string
    type: 'success' | 'error'
    action?: { label: string; onClick: () => void }
  } | null>(null)
  const { syncStatus } = useSyncStatus()

  useEffect(() => {
    fetchUsers()
    if (canManageInvites) {
      fetchInvites()
    } else {
      setInvites([])
    }
  }, [scenarioId, canManageInvites])

  const fetchUsers = async () => {
    setIsLoadingUsers(true)
    try {
      const data = await httpClient.listScenarioUsers(scenarioId)
      setUsers(data.users || [])
    } catch (err) {
      console.error('Error fetching users:', err)
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const fetchInvites = async () => {
    if (!canManageInvites) {
      setInvites([])
      return
    }
    try {
      const data = await httpClient.listInvites(scenarioId)
      setInvites(data.invites || [])
    } catch (err) {
      console.error('Error fetching invites:', err)
    }
  }

  const inviteUser = async () => {
    if (!email.trim()) {
      setError('Please enter an email address')
      return
    }

    setIsInviting(true)
    setError(null)

    try {
      const invite = await httpClient.createInvite(scenarioId, email.trim(), 'Editor')
      const trimmedEmail = email.trim()

      const copyLink = async () => {
        try {
          if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(invite.inviteUrl)
          } else {
            // Fallback for older browsers
            const textarea = document.createElement('textarea')
            textarea.value = invite.inviteUrl
            textarea.setAttribute('readonly', '')
            textarea.style.position = 'fixed'
            textarea.style.left = '-9999px'
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
          }

          setToast({ message: `Invite link copied for ${trimmedEmail}`, type: 'success' })
        } catch {
          setToast({ message: 'Could not copy invite link', type: 'error' })
        }
      }

      setToast({
        message: `Invite sent to ${trimmedEmail}`,
        type: 'success',
        action: invite.inviteUrl
          ? {
              label: 'Copy link',
              onClick: () => void copyLink(),
            }
          : undefined,
      })
      setEmail('')
      fetchUsers()
      if (canManageInvites) fetchInvites()
    } catch (err: any) {
      console.error('Failed to send invite:', err)
      // Extract error message from ApiError
      const message = err?.message || 'Failed to send invite. Please try again.'
      setError(message.includes('API request failed') ? 'Failed to send invite. Please try again.' : message)
    } finally {
      setIsInviting(false)
    }
  }

  const removeUser = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user from the scenario?')) {
      return
    }

    try {
      await httpClient.removeCollaborator(scenarioId, userId)
      fetchUsers()
    } catch (err) {
      alert('Failed to remove user. Please try again.')
    }
  }

  const revokeInvite = async (inviteId: string) => {
    if (!confirm('Are you sure you want to revoke this invite?')) {
      return
    }

    try {
      await httpClient.revokeInvite(scenarioId, inviteId)
      fetchInvites()
    } catch (err) {
      alert('Failed to revoke invite. Please try again.')
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Share "{scenarioName}"</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-banner">
              {error}
            </div>
          )}

          <div className="invite-section">
            {canManageInvites ? (
              <>
                {syncStatus === 'offline' ? (
                  <div className="offline-message" role="status" aria-live="polite">
                    ☁ You're offline. Sending invites requires an internet connection.
                  </div>
                ) : (
                  <div className="invite-form">
                    <input
                      type="email"
                      placeholder="Enter email to invite as editor"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && inviteUser()}
                      className="form-input"
                    />
                    <button
                      onClick={inviteUser}
                      disabled={isInviting || !email.trim()}
                      className="primary-btn"
                    >
                      {isInviting ? 'Sending...' : 'Send Invite'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ opacity: 0.9, fontSize: '0.95rem' }}>
                Only the scenario owner can send invites.
              </div>
            )}
          </div>

          <AccessListSection
            title="People with access"
            items={users.map((user) => ({
              id: user.userId,
              icon: <UserAvatar name={user.name} email={user.email} size="small" />,
              primaryText: user.name || user.email,
              secondaryText: user.email,
              actionButton: (
                <>
                  <div className="user-role">{user.role}</div>
                  {canManageInvites && user.role !== 'Owner' && (
                    <button
                      onClick={() => removeUser(user.userId)}
                      className="remove-btn"
                      title="Remove user"
                    >
                      ×
                    </button>
                  )}
                </>
              )
            }))}
            emptyMessage="No additional users yet"
            isLoading={isLoadingUsers}
            className="users-section"
          />

          {canManageInvites && invites.length > 0 && (
            <AccessListSection
              title="Pending invites"
              items={invites.map((invite) => ({
                id: invite.inviteId,
                icon: '📧',
                primaryText: invite.email,
                secondaryText: 'Invite sent',
                actionButton: (
                  canManageInvites ? (
                    <button
                      onClick={() => revokeInvite(invite.inviteId)}
                      className="revoke-btn"
                      title="Revoke invite"
                    >
                      ×
                    </button>
                  ) : null
                )
              }))}
              className="invites-section"
            />
          )}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          action={toast.action}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}
